/**
 * Helpers partagés pour AdminImmeubleExpenseModal + AdminImmeubleManualRevenueModal :
 *   - useCascadingSelectors hook : cascading Master → Property → Unit → Tenant
 *   - MasterPropertyUnitSelectors : composant réutilisable
 *   - ModalShell : wrapper standard
 *
 * Extrait de AdminImmeubleModals.jsx (Batch #14.221 — refactor oversized 2026-02).
 */
import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "@/lib/toast";
import { Loader2, X, Building2, DoorOpen, User } from "lucide-react";
import { API, authH } from "@/api";


// ────────────────────────────────────────────────────────────────────────────
// Helper hook : cascading dropdowns Master → Property → Unit → Tenant
// ────────────────────────────────────────────────────────────────────────────
function useCascadingSelectors({ initialMasterId = "", withTenants = false } = {}) {
  const [masters, setMasters] = useState([]);
  const [properties, setProperties] = useState([]);
  const [units, setUnits] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [masterId, setMasterId] = useState(initialMasterId);
  const [propertyId, setPropertyId] = useState("");
  const [unitId, setUnitId] = useState("");
  const [tenantId, setTenantId] = useState("");
  // Loading states per cascade (Batch #14.8 — UX clarity)
  const [loadingProperties, setLoadingProperties] = useState(false);
  const [loadingUnits, setLoadingUnits] = useState(false);
  const [loadingTenants, setLoadingTenants] = useState(false);

  // Load masters once
  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/admin/clients?limit=500`, { headers: authH() });
        setMasters(r.data?.items || []);
      } catch {
        toast.error("Erreur chargement clients");
      }
    })();
  }, []);

  // Load properties when master changes
  useEffect(() => {
    if (!masterId) { setProperties([]); setPropertyId(""); return; }
    setLoadingProperties(true);
    (async () => {
      try {
        const r = await axios.get(`${API}/admin/pms/master/${masterId}/properties`, { headers: authH() });
        setProperties(r.data?.items || []);
        setPropertyId("");
        setUnitId("");
        setTenantId("");
      } catch {
        toast.error("Erreur chargement immeubles");
      } finally {
        setLoadingProperties(false);
      }
    })();
  }, [masterId]);

  // Load units when property changes
  useEffect(() => {
    if (!propertyId) { setUnits([]); setUnitId(""); return; }
    setLoadingUnits(true);
    (async () => {
      try {
        const r = await axios.get(`${API}/admin/pms/property/${propertyId}/units`, { headers: authH() });
        setUnits(r.data?.items || []);
        setUnitId("");
        setTenantId("");
      } catch {
        toast.error("Erreur chargement unités/logements");
      } finally {
        setLoadingUnits(false);
      }
    })();
  }, [propertyId]);

  // Load tenants when unit changes (optional)
  useEffect(() => {
    if (!withTenants || !unitId) { setTenants([]); setTenantId(""); return; }
    setLoadingTenants(true);
    (async () => {
      try {
        const r = await axios.get(`${API}/admin/pms/unit/${unitId}/tenants`, { headers: authH() });
        setTenants(r.data?.items || []);
        setTenantId("");
      } catch {
        // silencieux : possible que l'endpoint n'existe pas encore
      } finally {
        setLoadingTenants(false);
      }
    })();
  }, [unitId, withTenants]);

  return {
    masters, properties, units, tenants,
    masterId, setMasterId,
    propertyId, setPropertyId,
    unitId, setUnitId,
    tenantId, setTenantId,
    loadingProperties, loadingUnits, loadingTenants,
  };
}

function MasterPropertyUnitSelectors({ ctx, includeUnitOptional = false, includeTenant = false, lockedMaster = false }) {
  const { masters, properties, units, tenants } = ctx;
  // Helpers d'état pour message contextuel
  const propPlaceholder = !ctx.masterId
    ? "Choisir un client d'abord"
    : ctx.loadingProperties
    ? "Chargement..."
    : (properties.length === 0 ? "Aucun immeuble pour ce client" : "— Sélectionner —");
  const unitPlaceholder = !ctx.propertyId
    ? "Choisir un immeuble d'abord"
    : ctx.loadingUnits
    ? "Chargement..."
    : (units.length === 0 ? "Aucune unité/logement créé(e)" : "— Sélectionner —");
  const tenantPlaceholder = !ctx.unitId
    ? "Choisir une unité d'abord"
    : ctx.loadingTenants
    ? "Chargement..."
    : (tenants.length === 0 ? "Aucun locataire actif" : "— Sélectionner —");

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      {!lockedMaster && (
        <div>
          <label className="text-xs font-semibold text-slate-600 mb-1 flex items-center gap-1.5">
            <User className="w-3.5 h-3.5" /> Client maître *
          </label>
          <select
            value={ctx.masterId}
            onChange={(e) => ctx.setMasterId(e.target.value)}
            className="w-full px-3 py-2 rounded-lg border border-slate-300 bg-white text-sm focus:ring-2 focus:ring-emerald-500"
            data-testid="admin-modal-master-select"
          >
            <option value="">— Sélectionner —</option>
            {masters.map((m) => (
              <option key={m.id} value={m.id}>
                {m.couple_display_name || m.display_name || m.individual_name || m.email || m.id}
              </option>
            ))}
          </select>
        </div>
      )}
      <div>
        <label className="text-xs font-semibold text-slate-600 mb-1 flex items-center gap-1.5">
          <Building2 className="w-3.5 h-3.5" /> Immeuble *
          {ctx.loadingProperties && <Loader2 className="w-3 h-3 animate-spin text-emerald-600" />}
        </label>
        <select
          value={ctx.propertyId}
          onChange={(e) => ctx.setPropertyId(e.target.value)}
          disabled={!ctx.masterId || ctx.loadingProperties}
          className="w-full px-3 py-2 rounded-lg border border-slate-300 bg-white text-sm focus:ring-2 focus:ring-emerald-500 disabled:bg-slate-50"
          data-testid="admin-modal-property-select"
        >
          <option value="">{propPlaceholder}</option>
          {properties.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name || p.address_line || p.id}
            </option>
          ))}
        </select>
      </div>
      <div>
        <label className="text-xs font-semibold text-slate-600 mb-1 flex items-center gap-1.5">
          <DoorOpen className="w-3.5 h-3.5" /> Logement (unité) {includeUnitOptional ? "(optionnel)" : "*"}
          {ctx.loadingUnits && <Loader2 className="w-3 h-3 animate-spin text-emerald-600" />}
        </label>
        <select
          value={ctx.unitId}
          onChange={(e) => ctx.setUnitId(e.target.value)}
          disabled={!ctx.propertyId || ctx.loadingUnits}
          className="w-full px-3 py-2 rounded-lg border border-slate-300 bg-white text-sm focus:ring-2 focus:ring-emerald-500 disabled:bg-slate-50"
          data-testid="admin-modal-unit-select"
        >
          <option value="">{unitPlaceholder}</option>
          {units.map((u) => (
            <option key={u.id} value={u.id}>Logement {u.unit_label}{u.unit_type ? ` — ${u.unit_type}` : ""}</option>
          ))}
        </select>
        {ctx.propertyId && !ctx.loadingUnits && units.length === 0 && (
          <p className="text-[10px] text-rose-600 mt-1" data-testid="admin-modal-unit-empty-hint">
            ⚠️ Aucun logement créé pour cet immeuble. Crée d'abord les unités via le Portail client → Immeuble → Gestion des unités.
          </p>
        )}
      </div>
      {includeTenant && (
        <div>
          <label className="text-xs font-semibold text-slate-600 mb-1 flex items-center gap-1.5">
            <User className="w-3.5 h-3.5" /> Locataire (optionnel)
            {ctx.loadingTenants && <Loader2 className="w-3 h-3 animate-spin text-emerald-600" />}
          </label>
          <select
            value={ctx.tenantId}
            onChange={(e) => ctx.setTenantId(e.target.value)}
            disabled={!ctx.unitId || ctx.loadingTenants}
            className="w-full px-3 py-2 rounded-lg border border-slate-300 bg-white text-sm focus:ring-2 focus:ring-emerald-500 disabled:bg-slate-50"
            data-testid="admin-modal-tenant-select"
          >
            <option value="">{tenantPlaceholder}</option>
            {tenants.map((t) => (
              <option key={t.id} value={t.id}>{t.display_name}</option>
            ))}
          </select>
        </div>
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Modal wrapper
// ────────────────────────────────────────────────────────────────────────────
function ModalShell({ open, onClose, title, subtitle, icon: Icon, iconBg, children, testId }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[100] bg-black/40 flex items-center justify-center p-4" onClick={onClose} data-testid={testId}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between p-5 border-b border-slate-200">
          <div className="flex items-center gap-3">
            <div className={`w-11 h-11 rounded-xl ${iconBg} flex items-center justify-center`}>
              <Icon className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-slate-800">{title}</h2>
              {subtitle && <p className="text-xs text-slate-500 mt-0.5">{subtitle}</p>}
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500" data-testid={`${testId}-close`}>
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// MODALE 1 — Saisie de dépense (Admin)
// ════════════════════════════════════════════════════════════════════════════
const EXPENSE_CATEGORIES = [
  { value: "entretien_reparation", label: "Entretien / Réparation" },
  { value: "renovations", label: "Rénovations" },
  { value: "taxes_municipales", label: "Taxes municipales" },
  { value: "taxes_scolaires", label: "Taxes scolaires" },
  { value: "assurance_immeuble", label: "Assurance immeuble" },
  { value: "assurance_responsabilite", label: "Assurance responsabilité" },
  { value: "energie_chauffage", label: "Énergie / Chauffage" },
  { value: "electricite_communes", label: "Électricité communes" },
  { value: "eau_aqueduc", label: "Eau / Aqueduc" },
  { value: "internet_telecom", label: "Internet / Télécom" },
  { value: "concierge_menage", label: "Concierge / Ménage" },
  { value: "deneigement_paysagement", label: "Déneigement / Paysagement" },
  { value: "extermination", label: "Extermination" },
  { value: "frais_juridiques", label: "Frais juridiques" },
  { value: "frais_comptables", label: "Frais comptables" },
  { value: "frais_administration", label: "Frais administration" },
  { value: "frais_bancaires_stripe", label: "Frais bancaires / Stripe" },
  { value: "interets_hypotheque", label: "Intérêts hypothèque" },
  { value: "publicite_marketing", label: "Publicité / Marketing" },
  { value: "petits_outils", label: "Petits outils" },
  { value: "fournitures_bureau", label: "Fournitures bureau" },
  { value: "autre", label: "Autre" },
];

const TAX_TYPES = [
  { value: "none", label: "Aucune taxe" },
  { value: "tps_only", label: "TPS seulement" },
  { value: "tvq_only", label: "TVQ seulement" },
  { value: "tps_tvq", label: "TPS + TVQ" },
  { value: "combined", label: "Combinée (HST)" },
  { value: "exempt", label: "Exemptée" },
];

export { useCascadingSelectors, MasterPropertyUnitSelectors, ModalShell, EXPENSE_CATEGORIES, TAX_TYPES, PAYMENT_METHODS, MONTHS_FR };

// PAYMENT_METHODS sont les modes de paiement reconnus.
// Les paiements électroniques apparaissent à la fois dans Comptabilité ET Données financières.
const PAYMENT_METHODS = [
  { value: "interac", label: "Interac e-Transfert" },
  { value: "e_transfert", label: "e-Transfert (autre)" },
  { value: "card", label: "Carte de crédit" },
  { value: "cash", label: "Comptant" },
  { value: "cheque", label: "Chèque" },
  { value: "transfer", label: "Virement bancaire" },
  { value: "other", label: "Autre" },
];

const MONTHS_FR = [
  "janvier", "février", "mars", "avril", "mai", "juin",
  "juillet", "août", "septembre", "octobre", "novembre", "décembre",
];
